// FEEL built-in functions — a practical subset of DMN 1.3. Focused on
// the families most used in decision tables: numeric, string, list,
// and boolean. Names use camelCase (FEEL spec uses "snake case with spaces"
// — the parser can't tolerate spaces in identifiers, so callers reference
// them via their camelCase form or `builtin("space name")`).

function asNum(x, name) {
  if (typeof x !== 'number' || Number.isNaN(x))
    throw new Error(`[feel/builtin ${name}] number expected`);
  return x;
}
function asStr(x, name) {
  if (typeof x !== 'string')
    throw new Error(`[feel/builtin ${name}] string expected`);
  return x;
}
function asList(x, name) {
  if (!Array.isArray(x))
    throw new Error(`[feel/builtin ${name}] list expected`);
  return x;
}

export const BUILTINS = {
  // ── numeric
  decimal:  (n, digits) => Number(asNum(n, 'decimal').toFixed(asNum(digits, 'decimal'))),
  floor:    (n) => Math.floor(asNum(n, 'floor')),
  ceiling:  (n) => Math.ceil(asNum(n, 'ceiling')),
  round:    (n) => Math.round(asNum(n, 'round')),
  abs:      (n) => Math.abs(asNum(n, 'abs')),
  modulo:   (a, b) => asNum(a, 'modulo') - asNum(b, 'modulo') * Math.floor(asNum(a, 'modulo') / asNum(b, 'modulo')),
  sqrt:     (n) => Math.sqrt(asNum(n, 'sqrt')),
  log:      (n) => Math.log(asNum(n, 'log')),
  exp:      (n) => Math.exp(asNum(n, 'exp')),
  odd:      (n) => asNum(n, 'odd') % 2 !== 0,
  even:     (n) => asNum(n, 'even') % 2 === 0,

  // ── list
  count:        (list) => asList(list, 'count').length,
  sum:          (list) => asList(list, 'sum').reduce((a, b) => a + asNum(b, 'sum'), 0),
  min:          (list) => Math.min(...asList(list, 'min').map((x) => asNum(x, 'min'))),
  max:          (list) => Math.max(...asList(list, 'max').map((x) => asNum(x, 'max'))),
  mean:         (list) => {
    const arr = asList(list, 'mean'); if (!arr.length) return null;
    return arr.reduce((a, b) => a + asNum(b, 'mean'), 0) / arr.length;
  },
  all:          (list) => asList(list, 'all').every(Boolean),
  any:          (list) => asList(list, 'any').some(Boolean),
  contains:     (list, elt) => asList(list, 'contains').some((x) => deepEqual(x, elt)),
  reverse:      (list) => asList(list, 'reverse').slice().reverse(),
  sort:         (list) => asList(list, 'sort').slice().sort((a, b) => (a < b ? -1 : a > b ? 1 : 0)),
  distinctValues:(list) => {
    const out = []; const seen = [];
    asList(list, 'distinctValues').forEach((x) => {
      if (!seen.some((y) => deepEqual(x, y))) { seen.push(x); out.push(x); }
    });
    return out;
  },
  indexOf: (list, match) => {
    const out = [];
    asList(list, 'indexOf').forEach((x, i) => { if (deepEqual(x, match)) out.push(i + 1); });
    return out;
  },
  append: (list, ...items) => [...asList(list, 'append'), ...items],
  concatenate: (...lists) => lists.reduce((a, l) => a.concat(asList(l, 'concatenate')), []),
  flatten: (list) => flat(asList(list, 'flatten')),

  // ── string
  stringLength: (s) => asStr(s, 'stringLength').length,
  upperCase:    (s) => asStr(s, 'upperCase').toUpperCase(),
  lowerCase:    (s) => asStr(s, 'lowerCase').toLowerCase(),
  substring:    (s, start, length) => {
    const str = asStr(s, 'substring');
    const st  = asNum(start, 'substring');
    // FEEL uses 1-based indexing; negative counts from end.
    let startIdx = st > 0 ? st - 1 : str.length + st;
    if (startIdx < 0) startIdx = 0;
    return length == null ? str.slice(startIdx) : str.slice(startIdx, startIdx + asNum(length, 'substring'));
  },
  substringBefore: (s, m) => {
    const str = asStr(s, 'substringBefore'); const match = asStr(m, 'substringBefore');
    const idx = str.indexOf(match); return idx < 0 ? '' : str.slice(0, idx);
  },
  substringAfter: (s, m) => {
    const str = asStr(s, 'substringAfter'); const match = asStr(m, 'substringAfter');
    const idx = str.indexOf(match); return idx < 0 ? '' : str.slice(idx + match.length);
  },
  startsWith: (s, m) => asStr(s, 'startsWith').startsWith(asStr(m, 'startsWith')),
  endsWith:   (s, m) => asStr(s, 'endsWith').endsWith(asStr(m, 'endsWith')),
  containsStr:(s, m) => asStr(s, 'contains').includes(asStr(m, 'contains')),
  matches:    (s, re) => new RegExp(asStr(re, 'matches')).test(asStr(s, 'matches')),
  replace:    (s, re, repl) => asStr(s, 'replace').replace(new RegExp(asStr(re, 'replace'), 'g'), asStr(repl, 'replace')),
  splitStr:   (s, sep) => asStr(s, 'split').split(asStr(sep, 'split')),
  stringJoin: (list, sep) => asList(list, 'stringJoin').join(asStr(sep || '', 'stringJoin')),

  // ── boolean / conversion
  not:     (v) => !truthy(v),
  string:  (v) => stringify(v),
  number:  (v) => {
    if (typeof v === 'number') return v;
    if (typeof v === 'string') { const n = Number(v); return Number.isNaN(n) ? null : n; }
    return null;
  },
  boolean: (v) => truthy(v),
};

// ── aliases matching the FEEL spec names (for easy string lookup) ─────────
export const BUILTIN_ALIASES = {
  'decimal':           'decimal',
  'floor':             'floor',
  'ceiling':           'ceiling',
  'round':             'round',
  'abs':               'abs',
  'modulo':            'modulo',
  'sqrt':              'sqrt',
  'log':               'log',
  'exp':               'exp',
  'odd':               'odd',
  'even':              'even',
  'count':             'count',
  'sum':               'sum',
  'min':               'min',
  'max':               'max',
  'mean':              'mean',
  'all':               'all',
  'any':               'any',
  'contains':          'containsStr', // ambiguous; we keep string semantics to match "contains(str, substr)"
  'listContains':      'contains',
  'reverse':           'reverse',
  'sort':              'sort',
  'distinct values':   'distinctValues',
  'distinctValues':    'distinctValues',
  'index of':          'indexOf',
  'indexOf':           'indexOf',
  'append':            'append',
  'concatenate':       'concatenate',
  'flatten':           'flatten',
  'string length':     'stringLength',
  'stringLength':      'stringLength',
  'upper case':        'upperCase',
  'upperCase':         'upperCase',
  'lower case':        'lowerCase',
  'lowerCase':         'lowerCase',
  'substring':         'substring',
  'substring before':  'substringBefore',
  'substringBefore':   'substringBefore',
  'substring after':   'substringAfter',
  'substringAfter':    'substringAfter',
  'starts with':       'startsWith',
  'startsWith':        'startsWith',
  'ends with':         'endsWith',
  'endsWith':          'endsWith',
  'matches':           'matches',
  'replace':           'replace',
  'split':             'splitStr',
  'splitStr':          'splitStr',
  'string join':       'stringJoin',
  'stringJoin':        'stringJoin',
  'not':               'not',
  'string':            'string',
  'number':            'number',
  'boolean':           'boolean',
};

export function resolveBuiltin(name) {
  const key = BUILTIN_ALIASES[name];
  if (key && BUILTINS[key]) return BUILTINS[key];
  if (BUILTINS[name]) return BUILTINS[name];
  return null;
}

// ── helpers (exported for the evaluator) ─────────────────────────────────

export function truthy(v) {
  if (v === null || v === undefined) return false;
  if (typeof v === 'boolean') return v;
  if (typeof v === 'number') return v !== 0;
  if (typeof v === 'string') return v.length > 0;
  if (Array.isArray(v)) return v.length > 0;
  return true;
}

export function deepEqual(a, b) {
  if (a === b) return true;
  if (a === null || b === null) return false;
  if (typeof a !== typeof b) return false;
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) if (!deepEqual(a[i], b[i])) return false;
    return true;
  }
  if (typeof a === 'object' && typeof b === 'object') {
    const ka = Object.keys(a), kb = Object.keys(b);
    if (ka.length !== kb.length) return false;
    return ka.every((k) => deepEqual(a[k], b[k]));
  }
  return false;
}

export function stringify(v) {
  if (v === null || v === undefined) return 'null';
  if (typeof v === 'string') return v;
  if (Array.isArray(v)) return '[' + v.map(stringify).join(', ') + ']';
  if (typeof v === 'object') {
    return '{' + Object.entries(v).map(([k, x]) => `${k}: ${stringify(x)}`).join(', ') + '}';
  }
  return String(v);
}

function flat(list) {
  const out = [];
  list.forEach((x) => {
    if (Array.isArray(x)) out.push(...flat(x));
    else out.push(x);
  });
  return out;
}
